/**
 * Upgrade Modal Context
 *
 * Provides a single, app-wide UpgradeModal that auto-opens whenever any API
 * call returns HTTP 402 (Payment Required). This eliminates per-component
 * boilerplate and guarantees that every subscription-gated endpoint surfaces
 * the upgrade prompt — even for features added in the future.
 *
 * How it works:
 *   1. The apiClient.ts 402 interceptor dispatches a `upgrade:required`
 *      CustomEvent with the reason string from the API response.
 *   2. This context listens for that event and opens the modal.
 *   3. Components that need to react when the modal closes (e.g. to retry
 *      a failed action after the user subscribes) can register a one-shot
 *      callback via `registerOnClose`.
 *   4. Components that need to know whether the modal is open (e.g. for
 *      action-replay guards) can read `upgradeOpened`.
 *
 * Non-402 upgrade flows (PricingPage "Subscribe" button, WelcomeOnboarding,
 * MyAccountPage, ContactSelectionModal) keep their own local UpgradeModal
 * instances — they are user-initiated, not API-driven.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { LazyUpgradeModal } from '../components/subscription/LazyUpgradeModal';
import {
  UpgradeModalContext,
  UPGRADE_REQUIRED_EVENT,
} from './UpgradeModalContext';

interface UpgradeModalProviderProps {
  children: ReactNode;
}

/**
 * Provider that mounts a single UpgradeModal at the app root and exposes
 * open/close controls via context. Listens for the `upgrade:required`
 * custom event dispatched by the apiClient interceptor on 402 responses.
 */
export const UpgradeModalProvider = ({ children }: UpgradeModalProviderProps) => {
  const [opened, setOpened] = useState(false);
  const [reason, setReason] = useState<string | undefined>();

  // One-shot close callbacks registered by consumers
  const onCloseCallbacksRef = useRef<Set<() => void>>(new Set());

  const openUpgrade = useCallback((upgradeReason?: string) => {
    // Idempotent: ignore if already open (prevents double-open from race conditions)
    setOpened((prev) => {
      if (prev) return prev;
      setReason(upgradeReason);
      return true;
    });
  }, []);

  const handleClose = useCallback(() => {
    setOpened(false);
    setReason(undefined);

    // Fire and clear all registered one-shot callbacks
    const callbacks = new Set(onCloseCallbacksRef.current);
    onCloseCallbacksRef.current.clear();
    callbacks.forEach((cb) => {
      try {
        cb();
      } catch (err) {
        console.error('[UpgradeModalContext] onClose callback error:', err);
      }
    });
  }, []);

  const registerOnClose = useCallback((cb: () => void) => {
    onCloseCallbacksRef.current.add(cb);
    return () => {
      onCloseCallbacksRef.current.delete(cb);
    };
  }, []);

  // Listen for the upgrade:required custom event from the API interceptor
  useEffect(() => {
    const handleUpgradeRequired = (event: Event) => {
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      openUpgrade(detail?.reason);
    };

    window.addEventListener(UPGRADE_REQUIRED_EVENT, handleUpgradeRequired);
    return () => window.removeEventListener(UPGRADE_REQUIRED_EVENT, handleUpgradeRequired);
  }, [openUpgrade]);

  return (
    <UpgradeModalContext.Provider value={{ upgradeOpened: opened, openUpgrade, registerOnClose }}>
      {children}
      <LazyUpgradeModal opened={opened} onClose={handleClose} reason={reason} />
    </UpgradeModalContext.Provider>
  );
};
